import {
  ApiChartAttribute,
  ApiChartCrawler,
  ApiChartHaving,
  ApiYAxisChartType,
} from "@incendium/api";
import { alpha } from "@mui/material";
import { getChartColor } from "../utils/utils";
import { IChart, IChartModel, IYKey, TChartData, TKeyValue } from "Interfaces";
import { LayoutType } from "recharts/types/util/types";
import theme from "theme";

// this model contains all business logic related to charts
// todo: move over all busniness logic related to charts 😜
// this includes things like what type of chart shoudl be rendered, if its bars, lines etc, spacing between bars and so on.
export default class ChartModel implements IChartModel {
  chart: IChart;

  constructor(chart: IChart) {
    this.chart = chart;
  }

  get yAxisKeys(): IYKey[] {
    return this.chart.yAxisKeys;
  }

  set yAxisKeys(value: IYKey[]) {
    this.chart.yAxisKeys = value;
  }

  get attributes(): ApiChartAttribute[] {
    return this.chart.attributes;
  }

  set attributes(value: ApiChartAttribute[]) {
    this.chart.attributes = value;
  }
  get crawler(): ApiChartCrawler | undefined {
    return this.chart.crawler;
  }

  set botType(value: ApiChartCrawler | undefined) {
    this.chart.crawler = value;
  }

  get having(): ApiChartHaving[] | undefined {
    return this.chart.having;
  }

  set having(value: ApiChartHaving[] | undefined) {
    this.chart.having = value;
  }

  private getConfiguredFields(): Set<string> | null {
    if (!this.yAxisKeys?.length) {
      return null;
    }

    const fields = new Set<string>();
    let hasObjectAxis = false;

    this.yAxisKeys.forEach((y) => {
      if (typeof y === "object" && y.fields?.length) {
        hasObjectAxis = true;
        y.fields.forEach((field) => fields.add(field));
      }
    });

    return hasObjectAxis ? fields : null;
  }

  private isConfiguredChartKey(key: string, configuredFields: Set<string>) {
    if (configuredFields.has(key)) {
      return true;
    }

    if (key.startsWith("previous_")) {
      return configuredFields.has(key.replace(/^previous_\\?/, ""));
    }

    return false;
  }

  private fieldBelongsToYAxis(key: string, fields: string[]) {
    if (fields.includes(key)) {
      return true;
    }

    if (key.startsWith("previous_")) {
      return fields.includes(key.replace(/^previous_\\?/, ""));
    }

    return false;
  }

  // todo: refactor, currently just copied from old code in component
  public getChartDataKeys(
    data: TChartData[],
    metricsOrder?: string[],
    noReOrder?: boolean
  ) {
    const rest = new Set(data.map(Object.keys).flat());
    rest.delete("name");
    rest.delete("fill");

    const configuredFields = this.getConfiguredFields();
    const arr = Array.from(rest)
      .filter((key) =>
        configuredFields ? this.isConfiguredChartKey(key, configuredFields) : true
      )
      .sort((a, b) =>
        metricsOrder
          ? metricsOrder.indexOf(a) - metricsOrder.indexOf(b)
          : noReOrder
          ? 0
          : a.localeCompare(b)
      )
      .sort((a, b) => {
        if (a.startsWith("previous_") && !b.startsWith("previous_")) {
          return 1;
        }
        if (!a.startsWith("previous_") && b.startsWith("previous_")) {
          return -1;
        }
        return 0;
      })
      .slice(0, 18); // todo: make a const or prop or maybe just remove

    return arr;
  }

  // todo: refactor, currently just copied from old code in component
  public getLinesStyles(
    data: TChartData[],
    colourMap: TKeyValue,
    metricsOrder?: string[],
    noReOrder?: boolean,
    fills?: TKeyValue,
    layout?: LayoutType
  ) {
    const chartDataKeys = this.getChartDataKeys(data, metricsOrder, noReOrder);

    const baseKeyColorMap: Record<string, string> = {};

    const styles = chartDataKeys.map((key, i) => {
      let id = `${key}-y`;
      let lineType = this.getLineType(key);
      let stackId: string | undefined = undefined;
      const isPrevious = key.startsWith("previous_");
      if (this.yAxisKeys) {
        this.yAxisKeys.forEach((y, i) => {
          if (
            typeof y === "object" &&
            this.fieldBelongsToYAxis(key, y.fields || [])
          ) {
            id = `${y.key}-y`;
            stackId = y.stackId !== "" ? y.stackId : undefined;
            return;
          }
        });
      }

      // fill logic, use colour map if set, otherwise fills if set (todo: remove one of these),
      // if these are not set we just use the keys index in theme, charts
      let fill =
        this.getFill(key, colourMap, fills) ||
        getChartColor(i, theme.palette.charts);

      // Check if this key is a "previous" version of another key
      if (isPrevious) {
        const baseKey = key.replace(/^previous_\\?/, "");
        // Use the same color as the base key if available, with reduced opacity
        if (baseKeyColorMap[baseKey]) {
          fill = alpha(baseKeyColorMap[baseKey], 0.6); // Apply opacity to the base key's color
        }
      } else {
        baseKeyColorMap[key] = fill;
      }

      const radius: [number, number, number, number] = stackId
        ? [0, 0, 0, 0]
        : layout === "vertical"
        ? [0, 5, 5, 0]
        : [5, 5, 0, 0];

      const cells = data.map((entry, index) => {
        let cellFill = entry.fill
          ? (entry.fill as string)
          : getCellFillByDimension(entry.name, colourMap, fill);
        return {
          key: `cell-${index}`,
          fill: cellFill,
          stroke: cellFill,
        };
      });

      return {
        id,
        key,
        lineType,
        stackId,
        fill,
        radius,
        cells,
        isPrevious: key.startsWith("previous_"),
      };
    });

    return styles;
  }

  private getLineType(key: string) {
    let type = ApiYAxisChartType.LINE;

    if (this.yAxisKeys) {
      this.yAxisKeys.forEach((y, i) => {
        if (
          typeof y === "object" &&
          this.fieldBelongsToYAxis(key, y.fields || [])
        ) {
          if (y.stackId && y.stackId !== "") {
            type =
              typeof y.chart === "object" && y.chart[key]
                ? y.chart[key]
                : ApiYAxisChartType.BAR;
          } else if (typeof y.chart === "string") {
            type = y.chart || ApiYAxisChartType.LINE;
          } else if (typeof y.chart === "object" && y.chart[key]) {
            type = y.chart[key];
          } else if (
            typeof y.chart === "object" &&
            key.startsWith("previous_")
          ) {
            const baseKey = key.replace(/^previous_\\?/, "");
            if (y.chart[baseKey]) {
              type = y.chart[baseKey];
            }
          }
          return;
        }
      });
    }
    return type;
  }

  private getFill(key: string, colourMap: TKeyValue, fills?: TKeyValue) {
    return colourMap && colourMap[key.split("\\")[0]]
      ? colourMap[key.split("\\")[0]]
      : fills && fills[key]
      ? fills[key]
      : undefined;
  }
}

export function getCellFillByDimension(
  name: string,
  colourMap: TKeyValue,
  fill: string
) {
  let cellFill =
    name && colourMap && colourMap[name.split("\\")[0]]
      ? colourMap[name.split("\\")[0]]
      : fill;
  return cellFill;
}
